import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { AlarmOutlined, CheckCircleOutline } from "@mui/icons-material";
import { getDeadlines } from "../api/deadlines";
import { daysChip } from "../pages/deadlines/deadlines";

const MAX_ROWS = 5;

// Overdue first (most overdue at the top), then due soon (closest first).
const byUrgency = (a, b) => a.days_left - b.days_left;

export default function DeadlineWidget() {
  const { data: user } = useSelector((state) => state.user);
  const [rows, setRows] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user?.token) return undefined;
    let cancelled = false;
    getDeadlines(user.token)
      .then(({ data }) => {
        if (cancelled) return;
        setRows((data.data || []).filter((d) => d.status === "OVERDUE" || d.status === "DUE_SOON").sort(byUrgency));
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const overdue = rows ? rows.filter((d) => d.status === "OVERDUE").length : 0;

  return (
    <Paper className="odr-card mt-4">
      <Box className="odr-card-header flex flex-wrap items-center justify-between gap-2">
        <Box className="flex items-center gap-2">
          <AlarmOutlined className="text-brand" />
          <Typography className="heading-title !text-base min-[600px]:!text-xl">Deadlines needing attention</Typography>
          {rows && rows.length ? (
            <Chip
              size="small"
              label={overdue ? `${overdue} overdue` : `${rows.length} due soon`}
              className={overdue ? "!bg-[#fdecea] !text-[#c62828]" : "!bg-[#fff4e0] !text-[#b45309]"}
            />
          ) : null}
        </Box>
        <Link to="/deadlines" className="text-[13px] font-semibold !text-brand">
          View all
        </Link>
      </Box>

      <Box className="px-4 pb-4">
        {failed ? (
          <Typography className="py-6 text-center text-[#6b7a90]">Deadlines couldn't be loaded.</Typography>
        ) : rows === null ? (
          <Box className="py-6 grid place-items-center">
            <CircularProgress size={24} />
          </Box>
        ) : !rows.length ? (
          <Box className="py-6 flex items-center justify-center gap-2 text-[#1b6b47]">
            <CheckCircleOutline fontSize="small" />
            <Typography className="text-[14px]">All deadlines are on track.</Typography>
          </Box>
        ) : (
          <ul className="m-0 p-0 list-none">
            {rows.slice(0, MAX_ROWS).map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 border-b border-solid border-b-[#e6eaf2] last:border-b-0">
                <Box className="min-w-0">
                  <Typography className="text-[14px] font-semibold text-[#0d1b36] truncate">{d.name}</Typography>
                  <Link to={`/timeline/${d.case_id}`} className="text-[12px] text-[#6b7a90] hover:underline">
                    {d.case_title}
                  </Link>
                </Box>
                <Box className="flex items-center gap-2">
                  {d.escalated_at ? (
                    <Chip size="small" variant="outlined" label="Escalated" className="!border-[#c62828] !text-[#c62828]" />
                  ) : null}
                  {daysChip(d)}
                </Box>
              </li>
            ))}
          </ul>
        )}
        {rows && rows.length > MAX_ROWS ? (
          <Typography className="pt-2 text-center text-[12px] text-[#6b7a90]">
            + {rows.length - MAX_ROWS} more on the Deadlines page
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}
